import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';

import { downloadReport } from '../../api/reportsAPI';
import { getSalesByDateRange } from '../../api/salesAPI';
import NavBar from '../layout/NavBar';
import './Reports.css';

const CSV = 'CSV';
const PDF = 'PDF';
const DISPLAY_ONLY = 'Display Only';
const SALES_BREAKDOWN = 'Sales Breakdown';

const REPORT_CHOICES = [
  {
    name: SALES_BREAKDOWN,
    includeDateStep: true,
    exports: [DISPLAY_ONLY, CSV, PDF],
  },
  {
    name: 'Service Export',
    includeDateStep: false,
    exports: [CSV, PDF],
  },
  {
    name: 'Customer Export',
    includeDateStep: false,
    exports: [CSV, PDF],
  },
  {
    name: 'Vehicle Export',
    includeDateStep: false,
    exports: [CSV, PDF],
  },
];

const PREDEFINED_RANGES = {
  THIS_MONTH: 'This Month',
  THIS_WEEK: 'This Week',
  THIS_YEAR: 'This Year',
  LAST_WEEK: 'Last Week',
  LAST_MONTH: 'Last Month',
  LAST_YEAR: 'Last Year',
  THIS_YEAR_Q1: 'This Year - Q1',
  THIS_YEAR_Q2: 'This Year - Q2',
  THIS_YEAR_Q3: 'This Year - Q3',
  THIS_YEAR_Q4: 'This Year - Q4',
  LAST_YEAR_Q1: 'Last Year - Q1',
  LAST_YEAR_Q2: 'Last Year - Q2',
  LAST_YEAR_Q3: 'Last Year - Q3',
  LAST_YEAR_Q4: 'Last Year - Q4',
};

function ReportCard({ title, children, className = '' }) {
  return (
    <Card variant='outlined' className={`report-card ${className}`.trim()}>
      <CardContent className='report-card__content'>
        <Typography variant='h5'>
          {title}
        </Typography>
        {children}
      </CardContent>
    </Card>
  );
}

function getRangeFromPredefined(range) {
  const now = dayjs();
  const startOf = (unit) => now.startOf(unit);
  const endOf = (unit) => now.endOf(unit);

  switch (range) {
    case PREDEFINED_RANGES.THIS_MONTH:
      return { start: startOf('month'), end: endOf('month') };
    case PREDEFINED_RANGES.LAST_MONTH: {
      const last = now.subtract(1, 'month');
      return { start: last.startOf('month'), end: last.endOf('month') };
    }
    case PREDEFINED_RANGES.THIS_WEEK:
      return { start: startOf('week'), end: endOf('week') };
    case PREDEFINED_RANGES.LAST_WEEK: {
      const last = now.subtract(1, 'week');
      return { start: last.startOf('week'), end: last.endOf('week') };
    }
    case PREDEFINED_RANGES.THIS_YEAR:
      return { start: startOf('year'), end: endOf('year') };
    case PREDEFINED_RANGES.LAST_YEAR: {
      const last = now.subtract(1, 'year');
      return { start: last.startOf('year'), end: last.endOf('year') };
    }
    case PREDEFINED_RANGES.THIS_YEAR_Q1:
      return { start: now.startOf('year'), end: now.startOf('year').add(2, 'month').endOf('month') };
    case PREDEFINED_RANGES.THIS_YEAR_Q2:
      return { start: now.startOf('year').add(3, 'month'), end: now.startOf('year').add(5, 'month').endOf('month') };
    case PREDEFINED_RANGES.THIS_YEAR_Q3:
      return { start: now.startOf('year').add(6, 'month'), end: now.startOf('year').add(8, 'month').endOf('month') };
    case PREDEFINED_RANGES.THIS_YEAR_Q4:
      return { start: now.startOf('year').add(9, 'month'), end: now.endOf('year') };
    case PREDEFINED_RANGES.LAST_YEAR_Q1: {
      const base = now.subtract(1, 'year').startOf('year');
      return { start: base, end: base.add(2, 'month').endOf('month') };
    }
    case PREDEFINED_RANGES.LAST_YEAR_Q2: {
      const base = now.subtract(1, 'year').startOf('year').add(3, 'month');
      return { start: base, end: base.add(2, 'month').endOf('month') };
    }
    case PREDEFINED_RANGES.LAST_YEAR_Q3: {
      const base = now.subtract(1, 'year').startOf('year').add(6, 'month');
      return { start: base, end: base.add(2, 'month').endOf('month') };
    }
    case PREDEFINED_RANGES.LAST_YEAR_Q4: {
      const base = now.subtract(1, 'year').startOf('year').add(9, 'month');
      return { start: base, end: base.endOf('year') };
    }
    default:
      return { start: startOf('month'), end: endOf('month') };
  }
}

function formatCurrency(value) {
  if (value == null || value === '') {
    return '-';
  }

  return `$${Number(value).toFixed(2)}`;
}

function formatCentralTime(value) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(date);
}

function slugifyReportName(reportName) {
  return reportName.toLowerCase().replace(/\s+/g, '-');
}

function formatDateForFile(dateValue) {
  return dateValue.format('YYYY-MM-DD');
}

function buildDownloadFilename(reportName, fileType, range = null) {
  const reportSlugByName = {
    [SALES_BREAKDOWN]: 'sales-breakdown',
    'Customer Export': 'customer-report',
    'Vehicle Export': 'vehicle-report',
    'Service Export': 'service-report',
  };
  const reportSlug = reportSlugByName[reportName] || slugifyReportName(reportName);
  const extension = fileType.toLowerCase();

  if (range) {
    return `${reportSlug}-${formatDateForFile(range.start)}-to-${formatDateForFile(range.end)}.${extension}`;
  }

  return `${reportSlug}-${dayjs().format('YYYY-MM-DD')}.${extension}`;
}

function triggerBlobDownload(blob, filename) {
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
}

export default function Reports() {
  const [selectedReportName, setSelectedReportName] = useState(REPORT_CHOICES[0].name);
  const [selectedDateRange, setSelectedDateRange] = useState(PREDEFINED_RANGES.THIS_MONTH);
  const [selectedDateStart, setSelectedDateStart] = useState(null);
  const [selectedDateEnd, setSelectedDateEnd] = useState(null);
  const [selectedExport, setSelectedExport] = useState(REPORT_CHOICES[0].exports[0]);
  const [salesResults, setSalesResults] = useState(null);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesError, setSalesError] = useState(null);
  const [reportError, setReportError] = useState(null);

  const selectedReport = useMemo(
    () => REPORT_CHOICES.find((report) => report.name === selectedReportName) || REPORT_CHOICES[0],
    [selectedReportName]
  );

  const exportStepTitle = `Step #${selectedReport.includeDateStep ? '3' : '2'} - Pick Output`;

  const handleReportChange = (event) => {
    const nextReport = REPORT_CHOICES.find((report) => report.name === event.target.value) || REPORT_CHOICES[0];

    setSelectedReportName(nextReport.name);
    setSelectedExport((currentExport) => (
      nextReport.exports.includes(currentExport) ? currentExport : nextReport.exports[0]
    ));
    setSalesResults(null);
    setSalesError(null);
    setReportError(null);
  };

  const handleDateRangeChange = (value, source) => {
    setReportError(null);
    setSalesError(null);

    if (source === 'predefinedRange') {
      setSelectedDateRange(value.target.value);
      return;
    }

    if (source === 'startDate') {
      setSelectedDateStart(value);
      return;
    }

    setSelectedDateEnd(value);
  };

  const handleExportChange = (event) => {
    setSelectedExport(event.target.value);
    setReportError(null);
    setSalesError(null);
  };

  const resolveSalesRange = () => {
    if (selectedDateStart && !selectedDateEnd) {
      throw new Error('Pick an end date to use a custom range.');
    }

    if (!selectedDateStart && selectedDateEnd) {
      throw new Error('Pick a start date to use a custom range.');
    }

    if (selectedDateStart && selectedDateEnd) {
      if (selectedDateStart.isAfter(selectedDateEnd, 'day')) {
        throw new Error('The start date must be on or before the end date.');
      }

      return {
        start: selectedDateStart.startOf('day'),
        end: selectedDateEnd.endOf('day'),
        label: `${selectedDateStart.format('MMM D, YYYY')} - ${selectedDateEnd.format('MMM D, YYYY')}`,
      };
    }

    const range = getRangeFromPredefined(selectedDateRange);
    return {
      ...range,
      label: selectedDateRange,
    };
  };

  const executeReport = async () => {
    setReportError(null);
    setSalesError(null);

    if (selectedReport.name === SALES_BREAKDOWN) {
      setSalesLoading(true);
    }

    try {
      if (selectedReport.name === SALES_BREAKDOWN) {
        const range = resolveSalesRange();
        const params = {
          start_date: range.start.toISOString(),
          end_date: range.end.toISOString(),
        };

        if (selectedExport === DISPLAY_ONLY) {
          const response = await getSalesByDateRange(params.start_date, params.end_date);
          setSalesResults({
            ...response.data,
            requestedRangeLabel: range.label,
          });
          return;
        }

        const downloadPath = selectedExport === CSV ? '/sales/csv/' : '/sales/pdf/';
        const preferredFilename = buildDownloadFilename(selectedReport.name, selectedExport, range);
        const { blob, filename } = await downloadReport(downloadPath, params, preferredFilename);
        triggerBlobDownload(blob, filename);
        return;
      }

      const reportPathByName = {
        'Customer Export': '/export/customers/',
        'Vehicle Export': '/export/vehicles/',
        'Service Export': '/export/services/',
      };

      const basePath = reportPathByName[selectedReport.name];
      const fileType = selectedExport.toLowerCase();
      const preferredFilename = buildDownloadFilename(selectedReport.name, selectedExport);
      const { blob, filename } = await downloadReport(`${basePath}${fileType}/`, {}, preferredFilename);
      triggerBlobDownload(blob, filename);
    } catch (error) {
      const message = selectedReport.name === SALES_BREAKDOWN && selectedExport === DISPLAY_ONLY
        ? error.response?.data?.error || 'Failed to load the sales report.'
        : 'Failed to generate the selected report.';

      if (selectedReport.name === SALES_BREAKDOWN && selectedExport === DISPLAY_ONLY) {
        setSalesError(message);
        setSalesResults(null);
      } else {
        setReportError(message);
      }
    } finally {
      if (selectedReport.name === SALES_BREAKDOWN) {
        setSalesLoading(false);
      }
    }
  };

  const salesContent = () => {
    if (salesLoading) {
      return (
        <Box className='report-loading'>
          <CircularProgress size={24} />
          <Typography>Loading sales data...</Typography>
        </Box>
      );
    }

    if (salesError) {
      return <Alert severity='error'>{salesError}</Alert>;
    }

    if (!salesResults) {
      return <Typography color='text.secondary'>Run the report to preview the sales breakdown.</Typography>;
    }

    const serviceRows = (salesResults.services || []).map((service, index) => ({
      ...service,
      date_time: formatCentralTime(service.date_time),
      payment_datetime: formatCentralTime(service.payment_datetime),
      service_id: service.service_id ?? service.id ?? index,
      service_item: service.service_item ?? (Array.isArray(service.service_items)
        ? service.service_items.join(', ')
        : service.service_items || '-'),
      service_row_key: service.service_row_key ?? `${service.service_id ?? service.id ?? index}-${index}`,
    }));

    return (
      <Box className='sales-results'>
        <Box className='sales-results__summary'>
          <Box>
            <Typography variant='h5'>Summary</Typography>
            <Typography color='text.secondary'>{salesResults.requestedRangeLabel}</Typography>
          </Box>
          <Box className='sales-results__totals'>
            <Typography>Total Orders: {salesResults.total_orders ?? '-'}</Typography>
            <Typography>Total Parts: {formatCurrency(salesResults.total_parts)}</Typography>
            <Typography>Total Labor: {formatCurrency(salesResults.total_labor)}</Typography>
            <Typography>Total Subtotal: {formatCurrency(salesResults.total_subtotal)}</Typography>
            <Typography>Total Tax: {formatCurrency(salesResults.total_tax)}</Typography>
            <Typography>Total Revenue: {formatCurrency(salesResults.total_revenue)}</Typography>
          </Box>
        </Box>

        <Typography variant='h6'>Revenue by Service Item</Typography>
        <TableContainer component={Paper} variant='outlined'>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell align='right'>Count</TableCell>
                <TableCell align='right'>Revenue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(salesResults.breakdown || []).length > 0 ? (
                salesResults.breakdown.map((row) => (
                  <TableRow key={`${row.label}-${row.count ?? 'tax'}`}>
                    <TableCell>{row.label ?? '-'}</TableCell>
                    <TableCell align='right'>{row.count ?? '-'}</TableCell>
                    <TableCell align='right'>{formatCurrency(row.revenue)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3} align='center'>
                    No completed sales were found for this range.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant='h6'>Completed Service Details</Typography>
        <TableContainer component={Paper} variant='outlined'>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Date/Time of Service</TableCell>
                <TableCell>Date/Time of Payment</TableCell>
                <TableCell>Branch</TableCell>
                <TableCell>Service ID</TableCell>
                <TableCell>Vehicle</TableCell>
                <TableCell>Service Item</TableCell>
                <TableCell align='right'>Parts</TableCell>
                <TableCell align='right'>Labor</TableCell>
                <TableCell align='right'>Subtotal</TableCell>
                <TableCell align='right'>Tax</TableCell>
                <TableCell align='right'>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {serviceRows.length > 0 ? (
                serviceRows.map((service) => (
                  <TableRow key={service.service_row_key}>
                    <TableCell>{service.date_time || '-'}</TableCell>
                    <TableCell>{service.payment_datetime || '-'}</TableCell>
                    <TableCell>{service.branch || '-'}</TableCell>
                    <TableCell>{service.service_id || '-'}</TableCell>
                    <TableCell>{service.vehicle || '-'}</TableCell>
                    <TableCell>{service.service_item || '-'}</TableCell>
                    <TableCell align='right'>{formatCurrency(service.parts_total)}</TableCell>
                    <TableCell align='right'>{formatCurrency(service.labor_total)}</TableCell>
                    <TableCell align='right'>{formatCurrency(service.service_subtotal)}</TableCell>
                    <TableCell align='right'>{formatCurrency(service.sales_tax_total)}</TableCell>
                    <TableCell align='right'>{formatCurrency(service.service_total)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align='center'>
                    No completed services were found for this range.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Box className='reports-page'>
      <Typography variant='h2' align='center'>Reports</Typography>
      <NavBar active='Reports' />

      <Box className='reports-grid'>
        <ReportCard title='Step #1 - Pick Report'>
          <FormControl>
            <RadioGroup
              value={selectedReport.name}
              onChange={handleReportChange}
              name='report-choice'
            >
              {REPORT_CHOICES.map((report) => (
                <FormControlLabel
                  key={report.name}
                  value={report.name}
                  control={<Radio size='small' />}
                  label={report.name}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </ReportCard>

        {selectedReport.includeDateStep && (
          <ReportCard title='Step #2 - Pick Date Range' className='report-card--date-range'>
            <Box className='report-date-grid'>
              <Box>
                <Typography>Predefined Range</Typography>
                <FormControl fullWidth sx={{ my: '10px' }}>
                  <Select
                    value={selectedDateRange}
                    onChange={(event) => handleDateRangeChange(event, 'predefinedRange')}
                  >
                    {Object.values(PREDEFINED_RANGES).map((range) => (
                      <MenuItem key={range} value={range}>{range}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant='body2' color='text.secondary'>
                  Use a custom range below to override the predefined selection.
                </Typography>
              </Box>

              <Typography className='report-date-grid__separator'>OR</Typography>

              <Box>
                <Typography>Custom Range</Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label='Start Date'
                    value={selectedDateStart}
                    onChange={(newDate) => handleDateRangeChange(newDate, 'startDate')}
                    sx={{ my: '10px', display: 'block', width: '100%' }}
                  />
                  <DatePicker
                    label='End Date'
                    value={selectedDateEnd}
                    onChange={(newDate) => handleDateRangeChange(newDate, 'endDate')}
                    sx={{ my: '10px', display: 'block', width: '100%' }}
                  />
                </LocalizationProvider>
              </Box>
            </Box>
          </ReportCard>
        )}

        <ReportCard title={exportStepTitle}>
          <FormControl>
            <RadioGroup
              value={selectedExport}
              onChange={handleExportChange}
              name='report-export'
            >
              {selectedReport.exports.map((exportOption) => (
                <FormControlLabel
                  key={exportOption}
                  value={exportOption}
                  control={<Radio size='small' />}
                  label={exportOption}
                />
              ))}
            </RadioGroup>
            <Button
              variant='contained'
              sx={{ marginTop: '10px' }}
              onClick={executeReport}
              disabled={salesLoading}
            >
              {selectedExport === DISPLAY_ONLY ? 'Preview Report' : 'Download Report'}
            </Button>
          </FormControl>
        </ReportCard>
      </Box>

      {reportError && (
        <Box className='reports-feedback'>
          <Alert severity='error'>{reportError}</Alert>
        </Box>
      )}

      {selectedReport.name === SALES_BREAKDOWN && selectedExport === DISPLAY_ONLY && (
        <Box className='reports-results'>
          <ReportCard title='Sales Breakdown Results' className='report-card--results'>
            {salesContent()}
          </ReportCard>
        </Box>
      )}
    </Box>
  );
}
